import {
  apiFetch,
  readJsonResponse,
  resolveApiBaseUrl,
} from './apiFetch';

export { readJsonResponse, resolveApiBaseUrl };

export function startEmailAuthentication(email) {
  return apiFetch('/api/auth/email/start', {
    method: 'POST',
    body: { email },
  });
}

export function verifyEmailAuthentication({ email, code }) {
  return apiFetch('/api/auth/email/verify', {
    method: 'POST',
    body: { email, code },
  });
}

export function logout() {
  return apiFetch('/api/auth/logout', {
    method: 'POST',
  });
}

export function startProviderLink(providerId, { returnTo = "/" } = {}) {
  return apiFetch(`/api/auth/link/start/${encodeURIComponent(providerId)}`, {
    method: 'POST',
    body: { returnTo },
  });
}

export function unlinkAuthIdentity(providerId) {
  return apiFetch(`/api/auth/identities/${encodeURIComponent(providerId)}`, {
    method: 'DELETE',
  });
}

// The raw file is the request body. No multipart, no base64: there is exactly
// one file and no form fields, so a multipart parser would add a dependency,
// filename handling and a temp-file lifecycle for no benefit. apiFetch leaves
// a Blob body untouched and still attaches X-CSRF-Token, whose custom header
// forces a preflight regardless of content type.
//
// The declared type is transport only. The backend decides acceptance from the
// bytes themselves and never trusts this value.
export function uploadUserAvatar(file) {
  return apiFetch('/api/profile/avatar', {
    method: 'PUT',
    body: file,
    headers: file?.type ? { 'Content-Type': file.type } : undefined,
  });
}

export function removeUserAvatar() {
  return apiFetch('/api/profile/avatar', {
    method: 'DELETE',
  });
}

export function startStepUpEmail() {
  return apiFetch('/api/auth/step-up/email/start', {
    method: 'POST',
    body: {},
  });
}

export function verifyStepUpEmail(code) {
  return apiFetch('/api/auth/step-up/email/verify', {
    method: 'POST',
    body: { code },
  });
}

function getLocalTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Toronto';
  } catch {
    return 'America/Toronto';
  }
}

function withoutCallerIdentity(payload = {}) {
  const { userId: _callerIdentity, ...body } = payload || {};
  return body;
}

export const fetchExercises = async ({
  q = '',
  limit = 25,
  bodyParts = [],
  muscleFocus = [],
  equipmentCategory = [],
  trainingType = [],
  difficulty = [],
  status = '',
} = {}) => {
    const params = new URLSearchParams();
    params.set('limit', String(limit));
    if (q) {
      params.set('q', q);
    }
    if (bodyParts.length) {
      params.set('bodyParts', bodyParts.join(','));
    }
    if (muscleFocus.length) {
      params.set('muscleFocus', muscleFocus.join(','));
    }
    if (equipmentCategory.length) {
      params.set('equipmentCategory', equipmentCategory.join(','));
    }
    if (trainingType.length) {
      params.set('trainingType', trainingType.join(','));
    }
    if (difficulty.length) {
      params.set('difficulty', difficulty.join(','));
    }
    if (status) {
      params.set('status', status);
    }

    let json;
    try {
      json = await apiFetch(`/api/exercises?${params.toString()}`);
    } catch (error) {
      // Preserve this legacy helper's intentionally minimal error contract.
      if (error?.status == null) {
        throw error;
      }
      throw new Error(`API error ${error?.status}`);
    }
  
    // Nouveau format seed: { items: [...], nextCursor, total }
    if (Array.isArray(json.items)) {
      return json.items;
    }
  
    // Ancien format DB: { success, data: [...] }
    if (Array.isArray(json.data)) {
      return json.data;
    }
  
    // Fallback: parfois l’API peut renvoyer directement un array
    if (Array.isArray(json)) {
      return json;
    }
  
    return [];
};

// Canonical muscle-contribution map, loaded once per session for local
// weighted-volume calculation: { rulesVersion, items: { [exerciseId]:
// { trainingType, muscleContribution } } }.
export function fetchMuscleContributions() {
  return apiFetch('/api/exercises/muscle-contributions');
}

export const fetchUserExercisePoolResponse = async (
  {
    q = '',
    limit = 25,
    cursor = '',
    bodyParts = [],
    muscleFocus = [],
    equipmentCategory = [],
    trainingType = [],
    difficulty = [],
    includeExcluded = false,
  } = {}
) => {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (q) {
    params.set('q', q);
  }
  if (cursor) {
    params.set('cursor', String(cursor));
  }
  if (bodyParts.length) {
    params.set('bodyParts', bodyParts.join(','));
  }
  if (muscleFocus.length) {
    params.set('muscleFocus', muscleFocus.join(','));
  }
  if (equipmentCategory.length) {
    params.set('equipmentCategory', equipmentCategory.join(','));
  }
  if (trainingType.length) {
    params.set('trainingType', trainingType.join(','));
  }
  if (difficulty.length) {
    params.set('difficulty', difficulty.join(','));
  }
  if (includeExcluded) {
    params.set('includeExcluded', 'true');
  }

  return apiFetch(`/api/exercise-pool?${params.toString()}`);
};

export const fetchUserExercisePool = async (params = {}) => {
  const response = await fetchUserExercisePoolResponse(params);

  return Array.isArray(response.items) ? response.items : [];
};
  

/**
 * Sauvegarde un programme (Phase 1 : Program + TrainingSessions).
 * Payload: { name, durationWeeks, sessionsPerWeek, sessions }
 */
export const savePlan = async (programPayload) => {
  let json;
  try {
    json = await apiFetch('/api/plans', {
      method: 'POST',
      body: withoutCallerIdentity(programPayload),
    });
  } catch (error) {
    // Preserve the legacy compatibility helper's plain Error contract.
    if (error?.status == null) {
      throw error;
    }
    throw new Error(error.message);
  }
  return json.data;
};

export async function createWeeklyPlanDraft(programPayload) {
  return apiFetch('/api/weekly-plans', {
    method: 'POST',
    body: withoutCallerIdentity(programPayload),
  });
}

export async function createAIWeeklyPlanDraft({ signal, generationId } = {}) {
  return apiFetch('/api/weekly-plans/ai-drafts', {
    method: 'POST',
    body: {
      ...(generationId ? { generationId } : {}),
    },
    ...(signal ? { signal } : {}),
  });
}

export async function getAIWeeklyPlanGenerationProgress(generationId, { signal } = {}) {
  return apiFetch(
    `/api/weekly-plans/ai-drafts/${encodeURIComponent(generationId)}/progress`,
    signal ? { signal } : undefined
  );
}

export async function getWeeklyPlans() {
  return apiFetch('/api/weekly-plans');
}

export async function getWeeklyPlanById(weeklyPlanParentId) {
  return apiFetch(`/api/weekly-plans/${weeklyPlanParentId}`);
}

export async function getUserSettings() {
  return apiFetch('/api/settings');
}

export async function getTrainingProfileOptions() {
  return apiFetch('/api/settings/options');
}

export async function updateUserProfile(profilePayload) {
  return apiFetch('/api/profile', {
    method: "PUT",
    body: withoutCallerIdentity(profilePayload),
  });
}

export async function updateUserOnboarding(onboardingPayload) {
  return apiFetch('/api/onboarding', {
    method: "PATCH",
    body: withoutCallerIdentity(onboardingPayload),
  });
}

export async function adoptOnboardingDraft(draft) {
  return apiFetch('/api/onboarding/adopt-draft', {
    method: "POST",
    body: draft,
  });
}

// `expectedRevision`: the `trainingProfile.revision` the draft was edited from.
// The backend then answers 409 TRAINING_PROFILE_CONFLICT, saving nothing, if
// the profile changed since. Without it the save replaces the whole profile,
// which the backend only allows until onboarding is complete (428 after).
export async function updateTrainingProfileSettings(
  trainingProfileDraft,
  { expectedRevision = null } = {}
) {
  const body = withoutCallerIdentity(trainingProfileDraft);
  return apiFetch('/api/settings/training-profile', {
    method: "PATCH",
    body: expectedRevision ? { ...body, expectedRevision } : body,
  });
}

export async function updateWorkoutPreferences(preferencesPatch) {
  return apiFetch('/api/settings/workout-preferences', {
    method: "PATCH",
    body: withoutCallerIdentity(preferencesPatch),
  });
}

export async function updateTrainingProfileAvailability(availability) {
  return updateTrainingProfileSettings({ availability });
}

export async function analyzeMovementConstraintsPainIssue(payload) {
  return apiFetch(
    '/api/settings/training-profile/movement-constraints/analyze',
    {
      method: "POST",
      body: withoutCallerIdentity(payload),
    }
  );
}

// `expectedDraftVersionId` (restore flows): only reopen that exact draft; the
// backend answers 409 DRAFT_PRECONDITION_FAILED, creating nothing, if it is gone.
// Without it this stays open-or-create.
export async function openOrCreateWeeklyPlanEditDraft(
  weeklyPlanParentId,
  { expectedDraftVersionId = null } = {}
) {
  return apiFetch(
    `/api/weekly-plans/${weeklyPlanParentId}/edit-draft`,
    expectedDraftVersionId
      ? { method: 'POST', body: { expectedDraftVersionId } }
      : { method: 'POST' }
  );
}

export async function updateWeeklyPlanDraft(
  weeklyPlanParentId,
  weeklyPlanVersionId,
  programPayload
) {
  return apiFetch(
    `/api/weekly-plans/${weeklyPlanParentId}/drafts/${weeklyPlanVersionId}`,
    {
      method: 'PATCH',
      body: withoutCallerIdentity(programPayload),
    }
  );
}

export async function saveWeeklyPlanWorkoutContent(
  weeklyPlanParentId,
  weeklyPlanVersionId,
  workoutId,
  payload
) {
  return apiFetch(
    `/api/weekly-plans/${weeklyPlanParentId}/drafts/${weeklyPlanVersionId}/workouts/${workoutId}`,
    {
      method: 'PATCH',
      body: withoutCallerIdentity(payload),
    }
  );
}

export async function publishWeeklyPlanDraft(weeklyPlanParentId) {
  return apiFetch(
    `/api/weekly-plans/${weeklyPlanParentId}/publish`,
    {
      method: 'POST',
    }
  );
}

export async function deleteWeeklyPlan(weeklyPlanParentId) {
  return apiFetch(`/api/weekly-plans/${weeklyPlanParentId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json' },
  });
}

export async function bookmarkWeeklyPlan(weeklyPlanParentId) {
  return apiFetch(
    `/api/weekly-plans/${weeklyPlanParentId}/bookmark`,
    {
      method: 'POST',
    }
  );
}

export async function unbookmarkWeeklyPlan(weeklyPlanParentId) {
  return apiFetch(
    `/api/weekly-plans/${weeklyPlanParentId}/bookmark`,
    {
      method: 'DELETE',
    }
  );
}

export async function createCycleFromWeeklyPlan(payload) {
  const body = withoutCallerIdentity(payload);
  return apiFetch('/api/cycles/from-weekly-plan', {
    method: 'POST',
    body: {
      ...body,
      timezone: body.timezone || getLocalTimezone(),
    },
  });
}

export async function getOnboardingCycleConflicts() {
  return apiFetch(
    `/api/cycles/conflicts?${new URLSearchParams({
      timezone: getLocalTimezone(),
    }).toString()}`
  );
}

export async function getCycleStartAvailability({
  candidateStartDates,
  durationWeeks,
}) {
  return apiFetch(
    `/api/cycles/conflicts?${new URLSearchParams({
      timezone: getLocalTimezone(),
      candidateStartDates: (candidateStartDates || []).join(','),
      durationWeeks: String(durationWeeks),
    }).toString()}`
  );
}

export async function getProgramsOverview() {
  return apiFetch(
    `/api/cycles/overview?${new URLSearchParams({
      timezone: getLocalTimezone(),
    }).toString()}`
  );
}

export async function getProgramOverviewV2() {
  return apiFetch(
    `/api/cycles/program-overview-v2?${new URLSearchParams({
      timezone: getLocalTimezone(),
    }).toString()}`
  );
}

function normalizeCycleLibraryItem(item) {
  // This only reshapes published-only overview cards for the library UI.
  // Do not merge draft builder payloads with canonical cycle fields here.
  return {
    id: item?.cycleId || item?.id,
    name: item?.name || 'Untitled cycle',
    editorialStatus: item?.editorialStatus || 'draft',
    temporalStatus: item?.temporalStatus || 'upcoming',
    startDate: item?.startDate || null,
    endDate: item?.endDate || null,
    durationWeeks: item?.durationWeeks || 0,
    sessionsPerWeek: item?.summary?.sessionsPerWeek || 0,
    totalWeeklySets:
      typeof item?.summary?.totalSetsFirstWeek === 'number'
        ? item.summary.totalSetsFirstWeek
        : null,
  };
}

export async function getAllCycles() {
  const overview = await getProgramsOverview();
  const orderedItems = [
    overview?.currentProgram,
    ...(overview?.upcomingPrograms || []),
    ...(overview?.pastPrograms || []),
  ].filter(Boolean);

  const dedupedItems = Array.from(
    new Map(orderedItems.map((item) => [item.cycleId || item.id, item])).values()
  );

  return {
    timezone: overview?.timezone || getLocalTimezone(),
    items: dedupedItems.map(normalizeCycleLibraryItem),
  };
}

export async function getCycleDetails(cycleId) {
  return apiFetch(
    `/api/cycles/${cycleId}?${new URLSearchParams({
      timezone: getLocalTimezone(),
    }).toString()}`
  );
}

// Draft/published separation is backend-owned. These builder wrappers return
// responses as-is; screens decide which backend field is authoritative.
// `options.expectedDraftPlanId` (restore flows): only reopen that exact draft;
// 409 DRAFT_PRECONDITION_FAILED, creating nothing, if it is gone.
export async function openOrCreateCycleEditDraft(cycleId, options = {}) {
  const body = withoutCallerIdentity(options);
  return apiFetch(`/api/cycles/${cycleId}/edit-draft`, {
    method: 'POST',
    body: {
      ...body,
      timezone: body.timezone || getLocalTimezone(),
    },
  });
}

export async function updateCycleDraft(cycleId, planId, payload) {
  const body = withoutCallerIdentity(payload);
  return apiFetch(`/api/cycles/${cycleId}/drafts/${planId}`, {
    method: 'PATCH',
    body: {
      ...body,
      timezone: body.timezone || getLocalTimezone(),
    },
  });
}

export async function saveCycleWorkoutContent(cycleId, planId, workoutId, payload) {
  const body = withoutCallerIdentity(payload);
  return apiFetch(
    `/api/cycles/${cycleId}/drafts/${planId}/workouts/${workoutId}`,
    {
      method: 'PATCH',
      body: {
        ...body,
        timezone: body.timezone || getLocalTimezone(),
      },
    }
  );
}

export async function updateUpcomingDraftTimeline(cycleId, planId, payload) {
  const body = withoutCallerIdentity(payload);
  return apiFetch(
    `/api/cycles/${cycleId}/drafts/${planId}/timeline`,
    {
      method: 'PATCH',
      body: {
        ...body,
        timezone: body.timezone || getLocalTimezone(),
      },
    }
  );
}

export async function publishCycleDraft(cycleId, options = {}) {
  const body = withoutCallerIdentity(options);
  return apiFetch(`/api/cycles/${cycleId}/publish`, {
    method: 'POST',
    body: {
      ...body,
      timezone: body.timezone || getLocalTimezone(),
    },
  });
}

export async function rescheduleUpcomingCycle(cycleId, payload) {
  // Deprecated for timeline truth: UI must use draft timeline save + publish.
  const body = withoutCallerIdentity(payload);
  return apiFetch(`/api/cycles/${cycleId}/reschedule`, {
    method: 'POST',
    body: {
      ...body,
      timezone: body.timezone || getLocalTimezone(),
    },
  });
}

export async function deleteCycle(cycleId) {
  return apiFetch(`/api/cycles/${cycleId}`, {
    method: 'DELETE',
  });
}

export async function getHomeDashboard(selectedDate) {
  const query = {
    timezone: getLocalTimezone(),
  };

  if (selectedDate) {
    query.selectedDate = selectedDate;
  }

  return apiFetch(
    `/api/home/dashboard?${new URLSearchParams(query).toString()}`
  );
}
